import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  getGiftDeliveryZoneSummary,
  searchGiftDeliveryZones,
} from '@shared/api/giftDeliveryZone/giftDeliveryZone.controller';
import {
  GiftDeliveryZoneItem,
  GiftDeliveryZoneSummary,
} from '@shared/api/giftDeliveryZone/giftDeliveryZone.interface';
import { GiftDeliveryZonePolicy } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import Modal from '@shared/components/Modal';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { formatPrice } from '@shared/utils/formatPrice';

interface IProps {
  openId: string;
  /** 권역 배송비 브랜드(giftItemBrand._id) */
  giftItemBrand: string;
}

//* 브랜드별 요약은 세션 동안 한 번만 받는다 (5천 건 집계라 매번 열 때마다 부르지 않음)
const summaryCache = new Map<string, GiftDeliveryZoneSummary>();

const SEARCH_LIMIT = 50;

/** 기본배송비 정책을 사람이 읽는 줄로 — "서울 7,000원 (45,000원 이상 무료)" */
const describeBaseFeeRules = (policy?: GiftDeliveryZonePolicy | null): string[] => {
  if (!policy) return [];
  const lines: string[] = [];
  (policy.baseFeeRules ?? []).forEach((rule) => {
    const where = rule.sidoIn?.length ? rule.sidoIn.join('·') : '지정 지역';
    lines.push(
      `${where} ${formatPrice(rule.fee)}원 (${formatPrice(
        rule.freeOverAmount,
      )}원 이상 무료)`,
    );
  });
  if (policy.defaultBaseFee) {
    const prefix = lines.length > 0 ? '그 외' : '기본';
    lines.push(
      `${prefix} ${formatPrice(policy.defaultBaseFee.fee)}원 (${formatPrice(
        policy.defaultBaseFee.freeOverAmount,
      )}원 이상 무료)`,
    );
  }
  return lines;
};

/**
 * 배송권역 안내 — "우리 동네 배송 되나, 얼마 붙나"를 결제 전에 확인하는 시트.
 * 읍면동/시군구 검색이 본체, 아래에 시도→시군구 요약. 검색 결과가 없으면 배송 불가 지역.
 */
const GiftDeliveryZoneGuideModal: FC<IProps> = ({ openId, giftItemBrand }) => {
  const open = useRecoilValue(simpleOpenAtomFamily(openId));

  const [summary, setSummary] = useState<GiftDeliveryZoneSummary | null>(
    () => summaryCache.get(giftItemBrand) ?? null,
  );
  const [summaryError, setSummaryError] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GiftDeliveryZoneItem[] | null>(null);
  const [resultCount, setResultCount] = useState(0);
  const [searching, setSearching] = useState(false);
  const [expandedSido, setExpandedSido] = useState<string | null>(null);
  const seqRef = useRef(0);

  // 열릴 때 요약 1회 로드
  useEffect(() => {
    if (!open || summary) return;
    let alive = true;
    getGiftDeliveryZoneSummary(giftItemBrand)
      .then((data) => {
        summaryCache.set(giftItemBrand, data);
        if (alive) setSummary(data);
      })
      .catch((err) => {
        console.error(err);
        if (alive) setSummaryError(true);
      });
    return () => {
      alive = false;
    };
  }, [open, summary, giftItemBrand]);

  // 검색 — 2글자부터, 300ms 디바운스
  useEffect(() => {
    const q = query.replace(/\s+/g, '');
    if (q.length < 2) {
      seqRef.current += 1;
      setResults(null);
      setSearching(false);
      return;
    }
    const seq = ++seqRef.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchGiftDeliveryZones({
          giftItemBrand,
          q,
          limit: SEARCH_LIMIT,
        });
        if (seq !== seqRef.current) return;
        setResults(res.items);
        setResultCount(res.count);
      } catch (err) {
        if (seq !== seqRef.current) return;
        console.error(err);
        setResults([]);
        setResultCount(0);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, giftItemBrand]);

  const baseFeeLines = useMemo(
    () => describeBaseFeeRules(summary?.zonePolicy),
    [summary],
  );
  const brandName = summary?.brand?.name ?? '이 브랜드';

  return (
    <Modal openId={openId} className="w-full">
      <div className="max-h-[80vh] overflow-y-auto">
        <h3 className="text-base font-bold">배송 가능 지역 · 배송비 안내</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {brandName} 상품은 배송지 읍·면·동에 따라 배송비가 다릅니다.
        </p>

        {/* 정책 요약 */}
        <div className="mt-3 rounded-lg bg-blue-50 p-3 text-sm">
          {summaryError ? (
            <p className="text-xs text-gray-500">
              안내를 불러오지 못했습니다. 잠시 후 다시 열어주세요.
            </p>
          ) : !summary ? (
            <p className="text-xs text-gray-500">불러오는 중…</p>
          ) : (
            <div className="space-y-1">
              {baseFeeLines.length > 0 && (
                <div className="flex gap-2">
                  <span className="w-16 flex-shrink-0 text-gray-500">기본배송비</span>
                  <div className="font-medium">
                    {baseFeeLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <span className="w-16 flex-shrink-0 text-gray-500">추가배송비</span>
                <p className="break-keep font-medium">
                  일부 지역은 주문 금액과 관계없이 지역 추가배송비가 붙습니다. 아래에서
                  동네를 검색해 확인하세요.
                </p>
              </div>
              {summary.zonePolicy?.unlistedZone !== 'allow' && (
                <div className="flex gap-2">
                  <span className="w-16 flex-shrink-0 text-gray-500">배송불가</span>
                  <p className="break-keep font-medium text-red-600">
                    목록에 없는 지역은 배송이 불가능합니다.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 검색 */}
        <div className="mt-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="읍·면·동 또는 시·군·구 검색 (예: 세곡동, 강릉시)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-red-400 focus:outline-none"
          />
          {query.replace(/\s+/g, '').length >= 2 && (
            <div className="mt-2">
              {searching && results === null ? (
                <p className="py-3 text-center text-xs text-gray-400">검색 중…</p>
              ) : results && results.length === 0 ? (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                  <p className="font-bold">검색 결과가 없습니다.</p>
                  <p className="mt-0.5 break-keep text-xs">
                    목록에 없는 지역은 배송이 불가능한 지역입니다. 다른
                    이름(법정동/행정동)으로도 검색해 보세요.
                  </p>
                </div>
              ) : results ? (
                <div className="overflow-hidden rounded-lg border">
                  <ul className="max-h-64 divide-y overflow-y-auto">
                    {results.map((zone) => (
                      <li
                        key={zone._id}
                        className="flex items-center justify-between px-3 py-2 text-sm"
                      >
                        <span>
                          <span className="text-gray-500">
                            {zone.sido} {zone.sigungu}{' '}
                          </span>
                          <span className="font-medium">{zone.emd}</span>
                        </span>
                        <span
                          className={`flex-shrink-0 text-xs font-bold ${
                            zone.extraFee > 0 ? 'text-orange-600' : 'text-green-600'
                          }`}
                        >
                          {zone.extraFee > 0
                            ? `추가 +${formatPrice(zone.extraFee)}원`
                            : '추가배송비 없음'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {resultCount > results.length && (
                    <p className="border-t bg-gray-50 px-3 py-1.5 text-[11px] text-gray-500">
                      {resultCount}곳 중 {results.length}곳 표시 — 더 정확한 이름으로
                      검색해 주세요.
                    </p>
                  )}
                </div>
              ) : null}
              <p className="mt-1 text-[11px] text-gray-400">
                * 표시 금액은 지역 추가배송비이며, 기본배송비는 주문 금액에 따라 별도
                적용됩니다.
              </p>
            </div>
          )}
        </div>

        {/* 시도 → 시군구 요약 */}
        {summary && summary.sidos.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs font-bold text-gray-600">
              배송 가능 지역 요약 (총 {formatPrice(summary.totalEmd)}개 읍·면·동)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {summary.sidos.map((sido) => (
                <button
                  key={sido.sido}
                  type="button"
                  onClick={() =>
                    setExpandedSido((prev) => (prev === sido.sido ? null : sido.sido))
                  }
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    expandedSido === sido.sido
                      ? 'border-red-400 bg-red-50 text-red-600'
                      : 'border-gray-300 text-gray-700'
                  }`}
                >
                  {sido.sido} <span className="text-gray-400">{sido.emdCount}</span>
                </button>
              ))}
            </div>
            {expandedSido && (
              <ul className="mt-2 max-h-56 divide-y overflow-y-auto rounded-lg border text-sm">
                {summary.sidos
                  .find((s) => s.sido === expandedSido)
                  ?.sigungus.map((sg) => (
                    <li
                      key={sg.sigungu}
                      className="flex items-center justify-between px-3 py-1.5"
                    >
                      <span>
                        {sg.sigungu || '(시군구 없음)'}{' '}
                        <span className="text-xs text-gray-400">{sg.emdCount}곳</span>
                      </span>
                      <span className="text-xs">
                        {sg.extraFeeCount === 0 ? (
                          <span className="text-green-600">추가배송비 없음</span>
                        ) : sg.extraFeeMin === sg.extraFeeMax ? (
                          <span className="text-orange-600">
                            추가 +{formatPrice(sg.extraFeeMax)}원
                            {sg.extraFeeCount < sg.emdCount ? ' (일부)' : ''}
                          </span>
                        ) : (
                          <span className="text-orange-600">
                            추가 {formatPrice(sg.extraFeeMin)}~
                            {formatPrice(sg.extraFeeMax)}원
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        {summary?.shippingFeeInfo && (
          <p className="mt-3 whitespace-pre-wrap break-keep text-[11px] text-gray-500">
            {summary.shippingFeeInfo}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default GiftDeliveryZoneGuideModal;
